export const LOCAL_STORAGE_KEY_APPSTACK_MATCHED = "hlwAppstackMatched";

const URL_APPSTACK_MATCH = "https://appstack.link/deeplink/match";

/**
 * Fires the Appstack deeplink match request that associates this browser
 * (device signals collected by Appstack on their ad-link redirect) with our
 * user id, which pipe uses as the `appstack_id` on all S2S events
 * (INSTALL / REGISTER / START_TRIAL / SUBSCRIBE).
 *
 * MUST run in the user's browser — Appstack only supports matching driven by
 * device-side data collection (server-side parameter matching is not shipped).
 * See https://docs.appstack.tech/admin/no-sdk-s2s-events.
 *
 * Fire-and-forget: never throws, no-ops when env vars are absent or the match
 * already succeeded for this user in this browser.
 */
export const matchAppstack = async ({
  onError,
  userId,
}: {
  onError?: (error: unknown) => void | Promise<void>;
  userId: number | string;
}): Promise<void> => {
  try {
    if (typeof window === "undefined") return;

    const apiKey = process.env.DOPPLER_PUBLIC_APPSTACK_API_KEY;
    const appId = process.env.DOPPLER_PUBLIC_APPSTACK_APP_ID;
    if (!apiKey || !appId) return;

    const localStorageKey = `${LOCAL_STORAGE_KEY_APPSTACK_MATCHED}:${userId}`;
    if (localStorage.getItem(localStorageKey)) return;

    const response = await fetch(
      `${URL_APPSTACK_MATCH}/${encodeURIComponent(appId)}?appstack_id=${encodeURIComponent(String(userId))}`,
      {
        method: "GET",
        headers: { "X-API-Key": apiKey },
        keepalive: true,
      },
    );

    if (response.ok)
      localStorage.setItem(localStorageKey, new Date().toISOString());
  } catch (error) {
    // Attribution must never break auth flows — swallow sync throws and
    // async rejections from the callback alike.
    try {
      await onError?.(error);
    } catch {
      // noop
    }
  }
};
